import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Stepper, Step, StepLabel, StepContent, Button, Alert,
  CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Chip,
  Grid, Card, CardContent, FormControl, InputLabel, Select, MenuItem, Snackbar,
  IconButton, LinearProgress, Tooltip, Divider
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { ingestionService } from '../services/ingestionService';
import { dataMappingService } from '../services/dataMappingService';
import { qualityThresholdService } from '../services/qualityThresholdService';
import { validationService } from '../services/validationService';

// Domain colors for semantic role chips
const DOMAIN_COLORS = {
  ghg:           { bg: '#e8f5e9', border: '#4caf50', text: '#2e7d32' },
  water:         { bg: '#e3f2fd', border: '#2196f3', text: '#1565c0' },
  biodiversity:  { bg: '#f3e5f5', border: '#9c27b0', text: '#7b1fa2' },
  social:        { bg: '#fff3e0', border: '#ff9800', text: '#e65100' },
  governance:    { bg: '#fce4ec', border: '#e91e63', text: '#c62828' },
  cross_domain:  { bg: '#f5f5f5', border: '#9e9e9e', text: '#616161' },
};

function AIMappingWizard() {
  const [activeStep, setActiveStep] = useState(0);
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [targetEntities, setTargetEntities] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Step results
  const [detectedSchema, setDetectedSchema] = useState(null);
  const [suggestedMappings, setSuggestedMappings] = useState(null);
  const [multiEntityPlan, setMultiEntityPlan] = useState(null); // Stage A: across-entities preview
  const [fieldOverrides, setFieldOverrides] = useState({}); // source_field -> new target_field
  const [expandedRows, setExpandedRows] = useState({});    // source_field -> bool (show reasoning)
  const [qualityResult, setQualityResult] = useState(null);
  const [validationResult, setValidationResult] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [filesData, entities] = await Promise.all([
        ingestionService.listFiles({ limit: 100 }),
        dataMappingService.getTargetEntities()
      ]);
      setFiles(filesData.files || []);
      setTargetEntities(entities || []);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load initial data', severity: 'error' });
    }
  };

  // Step 1: Schema Detection
  const handleDetectSchema = async () => {
    if (!selectedFile) return;
    try {
      setLoading(true);
      const schema = await dataMappingService.detectSchema(selectedFile.file_id);
      setDetectedSchema(schema);
      setActiveStep(1);
      setSnackbar({ open: true, message: 'Schema detected by AI', severity: 'success' });
    } catch (err) {
      const errMsg = err.response?.data?.error;
      setSnackbar({ open: true, message: (typeof errMsg === 'string' ? errMsg : errMsg?.message) || 'Schema detection failed', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Suggest Mappings
  const handleSuggestMappings = async () => {
    if (!selectedFile || !selectedEntity) return;
    try {
      setLoading(true);
      const mappings = await dataMappingService.suggestMappings(
        selectedFile.file_id, selectedEntity,
        selectedFile.sourceSystem?.system_type
      );
      setSuggestedMappings(mappings);
      setFieldOverrides({});
      setExpandedRows({});
      setActiveStep(2);
      setSnackbar({ open: true, message: 'AI mapping suggestions generated', severity: 'success' });
    } catch (err) {
      const errMsg = err.response?.data?.error;
      setSnackbar({ open: true, message: (typeof errMsg === 'string' ? errMsg : errMsg?.message) || 'Mapping suggestion failed', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Step 2 (alt): Multi-entity suggestion — route source fields across the whole
  // canonical OFP model (master + reference + transactional) instead of one entity.
  const handleSuggestMultiEntity = async () => {
    if (!selectedFile) return;
    try {
      setLoading(true);
      const plan = await dataMappingService.suggestMultiEntity(
        selectedFile.file_id,
        selectedFile.sourceSystem?.system_type
      );
      setMultiEntityPlan(plan);
      const n = (plan.entities || []).reduce((s, e) => s + e.field_mappings.length, 0);
      setSnackbar({
        open: true,
        message: `Mapped ${n} field(s) across ${plan.entities?.length || 0} OFP entities${plan.ai_status === 'unavailable' ? ' (AI unavailable — role/name match)' : ''}`,
        severity: 'success'
      });
    } catch (err) {
      const errMsg = err.response?.data?.error;
      setSnackbar({ open: true, message: (typeof errMsg === 'string' ? errMsg : errMsg?.message) || 'Multi-entity mapping failed', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Confirm Mappings
  const handleConfirmMappings = async () => {
    if (!suggestedMappings) return;
    try {
      setLoading(true);
      // Apply overrides to field mappings, track them for feedback
      const finalMappings = (suggestedMappings?.field_mappings ?? []).map(m => {
        const override = fieldOverrides[m.source_field];
        return override ? { ...m, target_field: override, overridden: true } : m;
      });
      const overrides = Object.entries(fieldOverrides).map(([source_field, newTarget]) => {
        const original = (suggestedMappings?.field_mappings ?? []).find(m => m.source_field === source_field);
        return { original: { source_field, target_field: original?.target_field }, corrected: { source_field, target_field: newTarget } };
      });
      await dataMappingService.confirmMappings(selectedFile.file_id, {
        target_ofp_entity: selectedEntity,
        field_mappings: finalMappings,
        source_system_type: selectedFile.sourceSystem?.system_type,
        ai_generated: true,
        ai_confidence: suggestedMappings.overall_confidence,
        human_reviewed: true,
        overrides
      });
      setActiveStep(3);
      setSnackbar({ open: true, message: 'Mapping confirmed and saved as template', severity: 'success' });
    } catch (err) {
      const errMsg = err.response?.data?.error;
      setSnackbar({ open: true, message: (typeof errMsg === 'string' ? errMsg : errMsg?.message) || 'Confirmation failed', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Step 4: Quality Assessment
  const handleQualityAssessment = async () => {
    try {
      setLoading(true);
      const result = await qualityThresholdService.evaluate(selectedFile.file_id);
      setQualityResult(result);
      setActiveStep(4);
      setSnackbar({ open: true, message: `Quality: ${(result.quality_score * 100).toFixed(1)}% (${result.quality_classification.replace(/_/g, ' ')})`, severity: 'success' });
    } catch (err) {
      const errMsg = err.response?.data?.error;
      setSnackbar({ open: true, message: (typeof errMsg === 'string' ? errMsg : errMsg?.message) || 'Quality assessment failed', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Step 5: Validation
  const handleRunValidation = async () => {
    try {
      setLoading(true);
      const result = await validationService.run(selectedFile.file_id);
      setValidationResult(result);
      setActiveStep(5);
      setSnackbar({
        open: true,
        message: result.overall_status === 'passed' ? 'Validation passed' : `Validation completed with ${result.findings_summary?.total || 0} findings`,
        severity: result.overall_status === 'passed' ? 'success' : 'warning'
      });
    } catch (err) {
      const errMsg = err.response?.data?.error;
      setSnackbar({ open: true, message: (typeof errMsg === 'string' ? errMsg : errMsg?.message) || 'Validation failed', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const confidenceColor = (c) => {
    if (c >= 0.8) return 'success';
    if (c >= 0.6) return 'warning';
    return 'error';
  };

  const eligibleFiles = files.filter(f => [
    'registered', 'schema_detected', 'mapping_suggested',
    'mapped', 'quality_assessed', 'validated', 'external'
  ].includes(f.status) || f.source_type === 'rtdip');

  return (
    <Box>
      <Typography variant="h4" gutterBottom>AI Data Mapping</Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        AI-powered pipeline: detect schema, map fields to OFP entities, assess quality, and validate
      </Typography>

      <Grid container spacing={3}>
        {/* Left: Wizard */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Stepper activeStep={activeStep} orientation="vertical">
              {/* Step 0: Select File */}
              <Step>
                <StepLabel>Select File & Detect Schema</StepLabel>
                <StepContent>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Select File</InputLabel>
                    <Select value={selectedFile?.file_id || ''} label="Select File"
                      onChange={(e) => setSelectedFile(files.find(f => f.file_id === e.target.value))}>
                      {eligibleFiles.map(f => (
                        <MenuItem key={f.file_id} value={f.file_id}>
                          {f.original_name || f.file_name} ({f.file_type}) - {(f.status || 'unknown').replace(/_/g, ' ')}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button variant="contained" onClick={handleDetectSchema} disabled={!selectedFile || loading}
                    startIcon={loading ? <CircularProgress size={16} /> : <SmartToyIcon />}>
                    Detect Schema with AI
                  </Button>
                </StepContent>
              </Step>

              {/* Step 1: Select Target Entity & Map */}
              <Step>
                <StepLabel>AI Field Mapping</StepLabel>
                <StepContent>
                  {detectedSchema && (
                    <Box mb={2}>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Detected <strong>{detectedSchema.fields?.length || 0} source fields</strong>.
                        Likely entity: <strong>{detectedSchema.likely_entity_type || detectedSchema.likely_ofp_entity || 'unknown'}</strong>
                        {detectedSchema.notes && ` — ${detectedSchema.notes}`}
                      </Alert>

                      {/* Source fields preview table */}
                      <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                        Detected Source Fields
                      </Typography>
                      <Table size="small" sx={{ mb: 2 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>Field Name</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Semantic Role</TableCell>
                            <TableCell>Confidence</TableCell>
                            <TableCell>Sample Values</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {detectedSchema.fields?.map((f, i) => {
                            const samples = detectedSchema.sample_rows
                              ? detectedSchema.sample_rows.slice(0, 3).map(r => r[f.field_name]).filter(v => v != null)
                              : [];
                            const domainStyle = DOMAIN_COLORS[f.domain] || DOMAIN_COLORS.cross_domain;
                            const conf = f.confidence || 0;
                            const odefLabel = f.odef_code ? `O-DEF: ${f.odef_code}` : null;
                            const matchInfo = f.match_source ? `Source: ${f.match_source}` : null;
                            const tooltipParts = [f.display_name, odefLabel, matchInfo].filter(Boolean).join('\n');
                            return (
                              <TableRow key={i}>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 600 }}>{f.field_name}</TableCell>
                                <TableCell><Chip label={f.inferred_type || 'unknown'} size="small" variant="outlined" /></TableCell>
                                <TableCell>
                                  <Tooltip title={tooltipParts || f.semantic_role} placement="right">
                                    <Chip
                                      label={f.semantic_role || 'unknown'}
                                      size="small"
                                      sx={{
                                        bgcolor: f.semantic_role && f.semantic_role !== 'unknown' ? domainStyle.bg : undefined,
                                        borderColor: f.semantic_role && f.semantic_role !== 'unknown' ? domainStyle.border : undefined,
                                        color: f.semantic_role && f.semantic_role !== 'unknown' ? domainStyle.text : undefined,
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                      }}
                                      variant="outlined"
                                    />
                                  </Tooltip>
                                </TableCell>
                                <TableCell sx={{ minWidth: 100 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <LinearProgress
                                      variant="determinate"
                                      value={conf * 100}
                                      color={conf >= 0.8 ? 'success' : conf >= 0.6 ? 'warning' : 'error'}
                                      sx={{ flex: 1, height: 6, borderRadius: 3 }}
                                    />
                                    <Typography variant="caption" sx={{ minWidth: 32, textAlign: 'right' }}>
                                      {(conf * 100).toFixed(0)}%
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'text.secondary' }}>
                                  {samples.length > 0 ? samples.join(' · ') : '—'}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>

                      {/* Co-occurrence flags */}
                      {detectedSchema.co_occurrence_flags?.length > 0 && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <Typography variant="subtitle2">Cross-field observations:</Typography>
                          {detectedSchema.co_occurrence_flags.map((flag, i) => (
                            <Typography key={i} variant="caption" display="block">
                              {flag.flag === 'warn' ? '\u26A0' : '\u2139'} {flag.message}
                              {flag.suggested_entity_type && ` (suggests: ${flag.suggested_entity_type})`}
                            </Typography>
                          ))}
                        </Alert>
                      )}

                      {/* Stage A: multi-entity mapping across the whole canonical model */}
                      <Box sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'primary.light', borderRadius: 1, bgcolor: 'rgba(25,118,210,0.04)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                          <Box>
                            <Typography variant="subtitle2">Map across all OFP entities (recommended)</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Routes each source field to the right entity across master / reference / transactional — not just one.
                            </Typography>
                          </Box>
                          <Button variant="contained" size="small" onClick={handleSuggestMultiEntity}
                            disabled={loading} startIcon={loading ? <CircularProgress size={16} /> : <SmartToyIcon />}>
                            Suggest across entities
                          </Button>
                        </Box>

                        {multiEntityPlan && (
                          <Box sx={{ mt: 2 }}>
                            <Alert severity={multiEntityPlan.ai_status === 'unavailable' ? 'warning' : 'success'} sx={{ mb: 1.5 }}>
                              {multiEntityPlan.entities?.reduce((s, e) => s + e.field_mappings.length, 0) || 0} field(s) mapped across{' '}
                              <strong>{multiEntityPlan.entities?.length || 0}</strong> entities · overall confidence{' '}
                              <strong>{((multiEntityPlan.overall_confidence || 0) * 100).toFixed(0)}%</strong>
                              {multiEntityPlan.ai_status === 'unavailable' && ' · AI unavailable (role/name match)'}
                              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                Preview only — confirm/seed across entities lands in a later step (Stage B).
                              </Typography>
                            </Alert>

                            {(multiEntityPlan.entities || []).map((ent) => {
                              const schemaColor = ent.ofp_schema === 'master_data' ? 'primary'
                                : ent.ofp_schema === 'reference_data' ? 'secondary' : 'success';
                              const sampleVal = (field) => {
                                for (const r of (detectedSchema?.sample_rows || [])) {
                                  const v = r[field];
                                  if (v !== null && v !== undefined && v !== '') return String(v);
                                }
                                return null;
                              };
                              return (
                                <Box key={ent.target_entity} sx={{ mb: 1.5, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                    <Chip label={ent.ofp_schema} size="small" color={schemaColor} variant="outlined" />
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                      {ent.target_entity.replace(/_/g, ' ')}
                                    </Typography>
                                    <Chip label={`${ent.field_mappings.length} field(s)`} size="small" variant="outlined" />
                                    <Chip label={`${((ent.confidence || 0) * 100).toFixed(0)}%`} size="small" color={confidenceColor(ent.confidence)} />
                                  </Box>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell sx={{ border: 0, py: 0.25, fontSize: '0.7rem', color: 'text.secondary' }}>Source field · value</TableCell>
                                        <TableCell sx={{ border: 0, py: 0.25 }} />
                                        <TableCell sx={{ border: 0, py: 0.25, fontSize: '0.7rem', color: 'text.secondary' }}>OFP target (schema.table.field)</TableCell>
                                        <TableCell sx={{ border: 0, py: 0.25 }} />
                                        <TableCell sx={{ border: 0, py: 0.25 }} />
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {ent.field_mappings.map((m, i) => {
                                        const val = sampleVal(m.source_field);
                                        return (
                                        <TableRow key={i}>
                                          <TableCell sx={{ border: 0, py: 0.25 }}>
                                            <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 600 }}>{m.source_field}</Typography>
                                            {val != null && (
                                              <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'text.secondary', ml: 1 }}>
                                                {val.length > 28 ? `${val.slice(0, 28)}…` : val}
                                              </Typography>
                                            )}
                                          </TableCell>
                                          <TableCell sx={{ border: 0, py: 0.25, color: 'text.secondary' }}>→</TableCell>
                                          <TableCell sx={{ border: 0, py: 0.25 }}>
                                            <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'text.secondary' }}>
                                              {ent.ofp_schema}.{ent.ofp_table}.
                                            </Typography>
                                            <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 600 }}>
                                              {m.target_field}
                                            </Typography>
                                          </TableCell>
                                          <TableCell sx={{ border: 0, py: 0.25 }}>
                                            {m.transformation ? <Chip label={m.transformation} size="small" variant="outlined" /> : null}
                                          </TableCell>
                                          <TableCell sx={{ border: 0, py: 0.25, width: 56 }}>
                                            <Chip label={`${((m.confidence || 0) * 100).toFixed(0)}%`} size="small" color={confidenceColor(m.confidence)} />
                                          </TableCell>
                                        </TableRow>
                                        );
                                      })}
                                    </TableBody>
                                  </Table>
                                </Box>
                              );
                            })}

                            {multiEntityPlan.unmapped_source_fields?.length > 0 && (
                              <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 1 }}>
                                <Typography variant="caption">Still unmapped:</Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                  {multiEntityPlan.unmapped_source_fields.map(f => (
                                    <Chip key={f} label={f} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
                                  ))}
                                </Box>
                              </Alert>
                            )}
                          </Box>
                        )}
                      </Box>

                      <Divider sx={{ my: 2 }}><Chip label="or map to a single entity" size="small" /></Divider>

                      <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Target OFP Entity</InputLabel>
                        <Select value={selectedEntity} label="Target OFP Entity"
                          onChange={(e) => setSelectedEntity(e.target.value)}>
                          {targetEntities.map(e => (
                            <MenuItem key={e.entity_type} value={e.entity_type}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {(e.entity_type || '').replace(/_/g, ' ')} ({e.field_count ?? 0} fields)
                                </Typography>
                                {e.ofp_schema && (
                                  <Typography variant="caption" color="text.secondary">
                                    {e.ofp_schema}.{e.ofp_table}
                                  </Typography>
                                )}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>

                      {/* Show target entity fields when selected */}
                      {selectedEntity && (() => {
                        const entityDef = targetEntities.find(e => e.entity_type === selectedEntity);
                        return entityDef ? (
                          <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Target OFP Fields: <em>{entityDef.description}</em>
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {entityDef.fields?.map(f => (
                                <Tooltip key={f.name} title={`${f.type} — ${f.description}`}>
                                  <Chip label={f.name} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }} />
                                </Tooltip>
                              ))}
                            </Box>
                          </Box>
                        ) : null;
                      })()}
                    </Box>
                  )}
                  <Button variant="contained" onClick={handleSuggestMappings}
                    disabled={!selectedEntity || loading}
                    startIcon={loading ? <CircularProgress size={16} /> : <SmartToyIcon />}>
                    Generate AI Mappings
                  </Button>
                </StepContent>
              </Step>

              {/* Step 2: Review & Confirm */}
              <Step>
                <StepLabel>Review & Confirm Mappings</StepLabel>
                <StepContent>
                  {suggestedMappings && (() => {
                    const entityDef = targetEntities.find(e => e.entity_type === selectedEntity);
                    const targetFieldOptions = entityDef?.fields?.map(f => f.name) || [];
                    const targetFieldDescs = Object.fromEntries((entityDef?.fields || []).map(f => [f.name, f.description]));
                    return (
                      <Box>
                        <Alert severity={suggestedMappings.overall_confidence >= 0.7 ? 'success' : 'warning'} sx={{ mb: 2 }}>
                          AI confidence: <strong>{(suggestedMappings.overall_confidence * 100).toFixed(1)}%</strong>
                          {suggestedMappings.unmapped_source_fields?.length > 0 &&
                            ` · ${suggestedMappings.unmapped_source_fields.length} source fields not mapped`}
                          {Object.keys(fieldOverrides).length > 0 &&
                            ` · ${Object.keys(fieldOverrides).length} field(s) manually overridden`}
                        </Alert>

                        <Table size="small" sx={{ mb: 2 }}>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ width: 30 }} />
                              <TableCell>Source Field</TableCell>
                              <TableCell>Sample Values</TableCell>
                              <TableCell>Target OFP Field</TableCell>
                              <TableCell>Target Description</TableCell>
                              <TableCell>Transform</TableCell>
                              <TableCell>Confidence</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {suggestedMappings.field_mappings?.map((m, i) => {
                              const currentTarget = fieldOverrides[m.source_field] ?? m.target_field;
                              const isOverridden = !!fieldOverrides[m.source_field];
                              const isExpanded = !!expandedRows[m.source_field];
                              const samples = detectedSchema?.sample_rows
                                ? detectedSchema.sample_rows.slice(0, 2).map(r => r[m.source_field]).filter(v => v != null)
                                : [];
                              return (
                                <React.Fragment key={i}>
                                  <TableRow
                                    sx={{
                                      bgcolor: m.requires_human_review && !isOverridden
                                        ? 'rgba(237,108,2,0.07)'
                                        : isOverridden ? 'rgba(25,118,210,0.06)' : 'inherit'
                                    }}>
                                    <TableCell sx={{ p: 0.5 }}>
                                      {(m.ai_reasoning || m.requires_human_review) && (
                                        <IconButton size="small" onClick={() => setExpandedRows(prev => ({ ...prev, [m.source_field]: !prev[m.source_field] }))}>
                                          {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                        </IconButton>
                                      )}
                                    </TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                      {m.source_field}
                                      {m.requires_human_review && !isOverridden && (
                                        <Tooltip title="AI flagged this mapping for human review">
                                          <WarningAmberIcon fontSize="small" color="warning" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                                        </Tooltip>
                                      )}
                                    </TableCell>
                                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem', color: 'text.secondary', maxWidth: 140 }}>
                                      {samples.length > 0 ? samples.join(' · ') : '—'}
                                    </TableCell>
                                    <TableCell>
                                      <FormControl size="small" fullWidth>
                                        <Select
                                          value={currentTarget}
                                          onChange={(e) => setFieldOverrides(prev => ({ ...prev, [m.source_field]: e.target.value }))}
                                          sx={{ fontFamily: 'monospace', fontSize: '0.82rem',
                                            '& .MuiOutlinedInput-notchedOutline': isOverridden ? { borderColor: 'primary.main', borderWidth: 2 } : {} }}>
                                          <MenuItem value=""><em>— unmapped —</em></MenuItem>
                                          {targetFieldOptions.map(tf => (
                                            <MenuItem key={tf} value={tf} sx={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{tf}</MenuItem>
                                          ))}
                                        </Select>
                                      </FormControl>
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.78rem', color: 'text.secondary', maxWidth: 160 }}>
                                      {targetFieldDescs[currentTarget]
                                        ? <Typography variant="caption">{targetFieldDescs[currentTarget]}</Typography>
                                        : '—'}
                                    </TableCell>
                                    <TableCell>
                                      {m.transformation ? <Chip label={m.transformation} size="small" variant="outlined" /> : '-'}
                                    </TableCell>
                                    <TableCell>
                                      <Chip label={`${(m.confidence * 100).toFixed(0)}%`} size="small"
                                        color={confidenceColor(m.confidence)} />
                                    </TableCell>
                                  </TableRow>
                                  {/* Expandable AI reasoning row */}
                                  {isExpanded && (m.ai_reasoning || m.requires_human_review) && (
                                    <TableRow>
                                      <TableCell colSpan={7} sx={{ pb: 1, pt: 0, bgcolor: 'action.hover' }}>
                                        <Box sx={{ pl: 2, fontSize: '0.82rem', color: 'text.secondary' }}>
                                          {m.requires_human_review && (
                                            <Typography variant="caption" color="warning.main" display="block" sx={{ fontWeight: 600 }}>
                                              ⚠ Flagged for review — low confidence or ambiguous field name
                                            </Typography>
                                          )}
                                          {m.ai_reasoning && (
                                            <Typography variant="caption" display="block">
                                              AI reasoning: {m.ai_reasoning}
                                            </Typography>
                                          )}
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </TableBody>
                        </Table>

                        {/* Unmapped source fields */}
                        {suggestedMappings.unmapped_source_fields?.length > 0 && (
                          <Alert severity="warning" icon={<InfoOutlinedIcon />} sx={{ mb: 2 }}>
                            <Typography variant="subtitle2">Unmapped source fields (not sent to OFP):</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                              {suggestedMappings.unmapped_source_fields.map(f => (
                                <Chip key={f} label={f} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
                              ))}
                            </Box>
                          </Alert>
                        )}

                        {suggestedMappings.notes && (
                          <Alert severity="info" sx={{ mb: 2 }}>{suggestedMappings.notes}</Alert>
                        )}
                      </Box>
                    );
                  })()}
                  <Button variant="contained" color="primary" onClick={handleConfirmMappings}
                    disabled={loading} startIcon={loading ? <CircularProgress size={16} /> : <CheckCircleIcon />}>
                    Confirm Mappings
                  </Button>
                </StepContent>
              </Step>

              {/* Step 3: Quality Assessment */}
              <Step>
                <StepLabel>Quality Assessment (PACT DQI)</StepLabel>
                <StepContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Evaluate data quality across 5 PACT dimensions with configurable thresholds per source system type.
                  </Typography>
                  <Button variant="contained" onClick={handleQualityAssessment}
                    disabled={loading} startIcon={loading ? <CircularProgress size={16} /> : <SmartToyIcon />}>
                    Run Quality Assessment
                  </Button>
                </StepContent>
              </Step>

              {/* Step 4: Validation */}
              <Step>
                <StepLabel>Pre-dVE Validation</StepLabel>
                <StepContent>
                  {qualityResult && (
                    <Alert severity={qualityResult.quality_classification === 'auto_accept' ? 'success' :
                      qualityResult.quality_classification === 'auto_reject' ? 'error' : 'warning'} sx={{ mb: 2 }}>
                      Quality: {(qualityResult.quality_score * 100).toFixed(1)}% - {(qualityResult.quality_classification || 'unknown').replace(/_/g, ' ')}
                    </Alert>
                  )}
                  <Button variant="contained" onClick={handleRunValidation}
                    disabled={loading} startIcon={loading ? <CircularProgress size={16} /> : <SmartToyIcon />}>
                    Run Validation
                  </Button>
                </StepContent>
              </Step>

              {/* Step 5: Results */}
              <Step>
                <StepLabel>Results</StepLabel>
                <StepContent>
                  {validationResult && (
                    <Box>
                      <Alert severity={validationResult.overall_status === 'passed' ? 'success' : 'warning'} sx={{ mb: 2 }}>
                        Status: {validationResult.overall_status} | Records: {validationResult.total_records} |
                        Passed: {validationResult.passed_records} | Failed: {validationResult.failed_records}
                      </Alert>
                      {validationResult.findings_summary && (
                        <Grid container spacing={1} sx={{ mb: 2 }}>
                          {Object.entries(validationResult.findings_summary.by_severity || {}).map(([sev, count]) => (
                            <Grid item key={sev}>
                              <Chip label={`${sev}: ${count}`} size="small"
                                color={sev === 'critical' ? 'error' : sev === 'high' ? 'error' : 'warning'} />
                            </Grid>
                          ))}
                        </Grid>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        {validationResult.findings_summary?.auto_fixable || 0} findings are auto-fixable.
                        File is ready for dVE submission when all critical findings are resolved.
                      </Typography>
                    </Box>
                  )}
                </StepContent>
              </Step>
            </Stepper>
          </Paper>
        </Grid>

        {/* Right: Context Panel */}
        <Grid item xs={12} md={4}>
          {selectedFile && (
            <Card elevation={1} sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Selected File</Typography>
                <Typography variant="body2"><strong>Name:</strong> {selectedFile.original_name || selectedFile.file_name}</Typography>
                <Typography variant="body2"><strong>Type:</strong> {selectedFile.file_type}</Typography>
                <Typography variant="body2"><strong>Status:</strong> {(selectedFile.status || 'unknown').replace(/_/g, ' ')}</Typography>
                <Typography variant="body2">
                  <strong>SoR:</strong> {selectedFile.elm_is_system_of_record ? 'ELM (Authoritative)' : 'External (Normalise)'}
                </Typography>
                {selectedFile.sourceSystem && (
                  <Typography variant="body2"><strong>Source:</strong> {selectedFile.sourceSystem.system_name}</Typography>
                )}
              </CardContent>
            </Card>
          )}

          {qualityResult && (
            <Card elevation={1} sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>Quality Breakdown</Typography>
                {qualityResult.breakdown?.normalised && Object.entries(qualityResult.breakdown.normalised).map(([dim, score]) => (
                  <Box key={dim} sx={{ mb: 1 }}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{dim}</Typography>
                      <Typography variant="body2">{(score * 100).toFixed(0)}%</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={score * 100}
                      color={score >= 0.7 ? 'success' : score >= 0.4 ? 'warning' : 'error'}
                      sx={{ height: 6, borderRadius: 3 }} />
                  </Box>
                ))}
              </CardContent>
            </Card>
          )}

          <Card elevation={1}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Pipeline Steps</Typography>
              <Typography variant="body2" color="text.secondary">
                1. Upload file via Ingestion Dashboard<br />
                2. AI detects source schema<br />
                3. AI suggests field mappings to OFP<br />
                4. Human reviews and confirms<br />
                5. PACT DQI quality assessment<br />
                6. Pre-dVE validation<br />
                7. Submit to dVE for verification
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default AIMappingWizard;
